import { IonButton, IonIcon, useIonRouter } from '@ionic/react';
import { createSelector } from '@reduxjs/toolkit';
import { qrCode, search } from 'ionicons/icons';
import React, { useCallback } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { t } from 'ttag';

import EventHeader from '@components/EventHeader';
import KioskLayout from '@components/KioskLayout';
import RoutePath from '@custom-types/RoutePath';
import { useCheckActionModal } from '@hooks/useCheckActionModal';
import { useScanner } from '@hooks/useScanner';
import { CheckAction, CheckEntity } from '@pages/Check/Enums';
import { formatDateRange } from '@services/date-service';
import { generateRoute } from '@services/navigation-service';
import { getEventConfigs, getEventInfo, getSearchEmailEnabled } from '@state/event';
import { getKioskMode } from '@state/kiosk';
import { getSessionInfo } from '@state/session';
import { getLocale } from '@state/system';
import { CapacitorBarcodeScannerCameraDirection } from '~capacitor-plugins/barcode-scanner';
import { Capacitor } from '~capacitor-plugins/core';

import { KioskMode } from './service';

const HeaderPanel = styled.div`
  width: 100%;
  height: 50%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
`;

const ButtonPanel = styled.div`
  width: 100%;
  height: 50%;

  @media (min-width: 768px) {
    display: flex;
    flex-direction: row;
    justify-content: center;
  }
`;

const Button = styled(IonButton)`
  margin-top: 16px;
  padding: 0px 16px;

  div {
    display: flex;
    flex-direction: row;
    align-items: center;
  }

  @media (min-width: 768px) {
    div {
      flex-direction: column;
    }
    ion-icon {
      margin-bottom: 10px;
      width: 48px;
      height: 48px;
    }
    width: 373px;
    height: 177px;
    font-size: 32px;
  }
`;

const Icon = styled(IonIcon)`
  margin-right: 5px;
  font-size: 32px;
`;

const selector = createSelector(
  getLocale,
  getEventInfo,
  getKioskMode,
  getSessionInfo,
  getEventConfigs,
  getSearchEmailEnabled,
  (
    locale,
    {
      id: eventId,
      name: eventName,
      imageUrl: eventImageUrl,
      startDate,
      endDate,
      location: eventLocation,
    },
    kioskMode,
    session,
    { idleScreenImage },
    searchEmailEnabled
  ) => ({
    locale,
    eventId,
    eventName,
    eventImageUrl,
    startDate,
    endDate,
    eventLocation,
    kioskMode,
    sessionId: session?.id,
    sessionName: session?.name,
    idleScreenImage,
    searchEmailEnabled,
  })
);

const Check = () => {
  const {
    locale,
    eventId,
    eventName,
    eventImageUrl,
    startDate,
    endDate,
    eventLocation,
    kioskMode,
    sessionId,
    sessionName,
    idleScreenImage,
    searchEmailEnabled,
  } = useSelector(selector);

  const { push: pushRoute } = useIonRouter();

  let checkAction: CheckAction | undefined;

  if (
    [
      KioskMode.EventCheckIn,
      KioskMode.SessionCheckIn,
      KioskMode.EventCheckInAndBadgePrinting,
    ].includes(kioskMode!)
  ) {
    checkAction = CheckAction.CheckIn;
  }

  if ([KioskMode.EventCheckOut, KioskMode.SessionCheckOut].includes(kioskMode!)) {
    checkAction = CheckAction.CheckOut;
  }

  const entityType = [KioskMode.SessionCheckIn, KioskMode.SessionCheckOut].includes(kioskMode!)
    ? CheckEntity.Session
    : CheckEntity.Event;

  const shouldPrintBadge = [
    KioskMode.BadgePrinting,
    KioskMode.EventCheckInAndBadgePrinting,
  ].includes(kioskMode!);

  const { show: showCheckModal } = useCheckActionModal({
    action: checkAction!,
    entityType,
    entityId: entityType === CheckEntity.Event ? eventId : sessionId!,
    title: entityType === CheckEntity.Event ? eventName : sessionName!,
  });

  const onScanned = useCallback(
    (code: string | undefined) => {
      if (!code) return;
      if (shouldPrintBadge) {
        pushRoute(
          generateRoute(RoutePath.PrintBadgeFlow, {
            eventId,
            checkInCode: code,
            shouldCheckIn: checkAction === CheckAction.CheckIn,
          })
        );
      } else {
        showCheckModal(code);
      }
    },
    [checkAction, eventId, pushRoute, shouldPrintBadge, showCheckModal]
  );

  const { scan } = useScanner({
    cameraDirection: CapacitorBarcodeScannerCameraDirection.FRONT,
    onScanned,
  });

  const background = idleScreenImage ? `url(${idleScreenImage?.fullSizeUrl})` : undefined;
  return (
    <KioskLayout footer={t`Powered by EventMobi™`} backgroundImage={background}>
      <HeaderPanel>
        {!background && (
          <EventHeader
            infoText={sessionName ? eventName : t`Welcome to`}
            name={sessionName || eventName}
            imageUrl={eventImageUrl}
            date={formatDateRange(startDate, endDate, locale)}
            location={eventLocation}
          />
        )}
      </HeaderPanel>
      <ButtonPanel>
        {Capacitor.isNativePlatform() && (
          <Button expand="block" size="large" onClick={scan}>
            <div>
              <Icon icon={qrCode} size="large" />
              {t`Scan QR Code`}
            </div>
          </Button>
        )}
        {searchEmailEnabled && (
          <Button
            expand="block"
            size="large"
            routerLink={generateRoute(RoutePath.KioskCheckBySearch, { eventId })}
          >
            <div>
              <Icon icon={search} size="large" />
              {t`Search`}
            </div>
          </Button>
        )}
      </ButtonPanel>
    </KioskLayout>
  );
};
export default Check;
